import React, { useState, useEffect } from "react"; // eslint-disable-line no-unused-vars
import { DonationInputs, Confirmation, Notification } from "../components";
import {
  LinearProgress,
  Grid2 as Grid,
  TextField,
  Button,
  Typography,
  Checkbox,
  Paper,
  Divider,
  FormControlLabel,
  Link,
} from "@mui/material";
import {
  CodeDetails,
  DefaultCodeDetails,
  Donation,
  Settings,
  DefaultSettings,
} from "../../../shared/types";
import {
  getSettings,
  fetchDonations,
  fetchCodeDetails,
  updateDonations,
  saveDonations,
} from "../../../shared/utilities/actions";
import { convertToCurrency } from "../../../shared/utilities/tools";
import * as consts from "../../../shared/utilities/consts";
import { getCardImage } from "../../../shared/utilities/actions";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";

export interface MainProps {}

export const Main = ({}: MainProps): React.JSX.Element => {
  const [codeDetails, setCodeDetails] = useState<CodeDetails>({
    ...DefaultCodeDetails,
  });
  const [src, SetSrc] = useState<Blob | null>(null);
  const [busy, setBusy] = useState<boolean>(true);
  const [shareSelections, setShareSelections] = useState<boolean>(true);
  const [allowTracking, setAllowTracking] = useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [donations, setDonations] = useState<Donation[]>([]);
  const [settings, setSettings] = useState<Settings>({ ...DefaultSettings });
  const [message, setMessage] = useState<string>("");
  const [trackingEmail, setTrackingEmail] = useState<string>("");
  const [notification, setNotification] = useState({ message: "", type: "" });

  useEffect(() => {
    const fetchData = async () => {
      setBusy(true);
      fetchDonations(setDonations);
      let themeId = fetchCodeDetails(codeDetails.themeId, setCodeDetails);
      let settingsResponse: Settings = await getSettings(themeId);
      setSettings(settingsResponse);
      let storedCheckout = localStorage.getItem(consts.STORAGE_CHECKOUT);
      if (storedCheckout) {
        let storedCheckoutObject: {
          message: string;
          share: boolean;
          allowTracking: boolean;
          trackingEmail: string;
        } = JSON.parse(storedCheckout);
        setMessage(storedCheckoutObject.message);
        setShareSelections(storedCheckoutObject.share);
        setAllowTracking(storedCheckoutObject.allowTracking);
        setTrackingEmail(storedCheckoutObject.trackingEmail);
      }
      setBusy(false);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      if (codeDetails?.code) {
        let srcResponse: Blob = await getCardImage(codeDetails.code);
        SetSrc(srcResponse);
      }
    };
    fetchData();
  }, [codeDetails]);

  let currentDonationAmount = 0;
  donations.map((donation) => {
    currentDonationAmount += donation.amount;
  });

  const validateDonation = (): boolean => {
    let valid = true;
    let remainingBalance =
      Math.round(
        (codeDetails.availableAmount - currentDonationAmount + Number.EPSILON) *
          100
      ) / 100;
    if (
      !settings.allowPartialSpend &&
      currentDonationAmount < codeDetails.availableAmount
    ) {
      valid = false;
      setNotification({
        message: "You must donate the full value of the gift card.",
        type: consts.ALERT_TYPE_ERROR,
      });
    } else if (
      remainingBalance != 0 &&
      remainingBalance < settings.donationMinimum
    ) {
      valid = false;
      setNotification({
        message: `A gift card cannot have a remaining balance less than $${settings.donationMinimum}.`,
        type: consts.ALERT_TYPE_ERROR,
      });
    } else if (remainingBalance < 0) {
      valid = false;
      setNotification({
        message: "You cannot donate more than the full value of the gift card.",
        type: consts.ALERT_TYPE_ERROR,
      });
    }
    return valid;
  };

  const handleRedirect = () => {
    if (settings.confirmationDetails.redirectUrl) {
      window.location.href = settings.confirmationDetails.redirectUrl;
    } else {
      window.location.href = "/causes";
    }
  };

  const renderGiftCardCheckout = () => {
    return (
      <Grid size={{ xs: 12, sm: 4 }}>
        <Paper sx={{ padding: "16px", marginBottom: "24px" }}>
          <Grid size={{ xs: 12 }} container spacing={2}>
            {codeDetails?.code && (
              <Grid size={{ xs: 12 }}>
                {src ? (
                  <img
                    src={URL.createObjectURL(src)}
                    style={{
                      width: "100%",
                      maxWidth: "500px",
                      margin: "auto",
                      border: "1px solid rgb(242 242 242)",
                      borderRadius: "22px",
                      backgroundColor: "rgb(242 242 242)",
                    }}
                  />
                ) : (
                  <img
                    src={`${consts.BLOB_URL}/v4-system-assets/CardImagePlaceholder.gif`}
                    style={{
                      width: "100%",
                      maxWidth: "500px",
                      margin: "auto",
                      border: "1px solid rgb(242 242 242)",
                      borderRadius: "22px",
                      backgroundColor: "rgb(242 242 242)",
                    }}
                  />
                )}
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <Button
                fullWidth={true}
                variant="outlined"
                onClick={() => {
                  window.location.href = "#dialog-donate";
                }}
                disabled={busy}
                className="tb_secondary_button"
              >
                View Gift
              </Button>
            </Grid>
            {donations.length > 0 && (
              <React.Fragment>
                <Grid size={{ xs: 12 }} container>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body1" style={{ paddingLeft: "4px" }}>
                      Original Balance:
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }} style={{ textAlign: "right" }}>
                    <Typography variant="body1" style={{ paddingRight: "4px" }}>
                      {convertToCurrency(codeDetails.originalAmount)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body1" style={{ paddingLeft: "4px" }}>
                      Donated:
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }} style={{ textAlign: "right" }}>
                    <Typography variant="body1" style={{ paddingRight: "4px" }}>
                      {convertToCurrency(
                        codeDetails.originalAmount - codeDetails.availableAmount
                      )}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }} style={{ margin: "8px 4px" }}>
                    <Divider />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body1" style={{ paddingLeft: "4px" }}>
                      Available Balance:
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }} style={{ textAlign: "right" }}>
                    <Typography variant="body1" style={{ paddingRight: "4px" }}>
                      {convertToCurrency(codeDetails.availableAmount)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body1" style={{ paddingLeft: "4px" }}>
                      Selected Charities:
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }} style={{ textAlign: "right" }}>
                    <Typography variant="body1" style={{ paddingRight: "4px" }}>
                      {`${currentDonationAmount > 0 ? "-" : ""}` +
                        convertToCurrency(currentDonationAmount)}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }} style={{ margin: "8px 4px" }}>
                    <Divider />
                  </Grid>
                  <Grid size={{ xs: 6 }}>
                    <Typography variant="body1" style={{ paddingLeft: "4px" }}>
                      Remaining Balance:
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 6 }} style={{ textAlign: "right" }}>
                    <Typography variant="body1" style={{ paddingRight: "4px" }}>
                      {convertToCurrency(
                        codeDetails.availableAmount - currentDonationAmount + 0
                      )}
                    </Typography>
                  </Grid>
                </Grid>
                {settings.captureThankYouMessage && (
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label={`Send a message to ${
                        codeDetails.cardFrom
                          ? codeDetails.cardFrom
                          : "your gifter"
                      }`}
                      value={message}
                      fullWidth={true}
                      multiline={true}
                      rows={5}
                      onChange={(
                        event: React.ChangeEvent<
                          HTMLInputElement | HTMLTextAreaElement
                        >
                      ) => {
                        let storedCheckout = localStorage.getItem(
                          consts.STORAGE_CHECKOUT
                        );
                        let storedCheckoutObject: {
                          message: string;
                          share: boolean;
                          allowTracking: boolean;
                          trackingEmail: string;
                        } = {
                          message: "",
                          share: true,
                          allowTracking: false,
                          trackingEmail: "",
                        };
                        if (storedCheckout) {
                          storedCheckoutObject = JSON.parse(storedCheckout);
                        }
                        storedCheckoutObject.message = event.target.value;
                        localStorage.setItem(
                          consts.STORAGE_CHECKOUT,
                          JSON.stringify(storedCheckoutObject)
                        );
                        setMessage(event.target.value);
                      }}
                    />
                  </Grid>
                )}
                {settings.optOutOfShareSelections && (
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={shareSelections}
                          disabled={busy}
                          onChange={(event) => {
                            let storedCheckout = localStorage.getItem(
                              consts.STORAGE_CHECKOUT
                            );
                            let storedCheckoutObject: {
                              message: string;
                              share: boolean;
                              allowTracking: boolean;
                              trackingEmail: string;
                            } = {
                              message: "",
                              share: true,
                              allowTracking: false,
                              trackingEmail: "",
                            };
                            if (storedCheckout) {
                              storedCheckoutObject = JSON.parse(storedCheckout);
                            }
                            storedCheckoutObject.share = event.target.checked;
                            localStorage.setItem(
                              consts.STORAGE_CHECKOUT,
                              JSON.stringify(storedCheckoutObject)
                            );
                            setShareSelections(event.target.checked);
                          }}
                        />
                      }
                      label={`Share the charities you selected with ${
                        codeDetails.cardFrom
                          ? codeDetails.cardFrom
                          : "your gifter"
                      }`}
                    />
                  </Grid>
                )}
                {settings.allowTracking && (
                  <Grid size={{ xs: 12 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={allowTracking}
                          disabled={busy}
                          onChange={(event) => {
                            let storedCheckout = localStorage.getItem(
                              consts.STORAGE_CHECKOUT
                            );
                            let storedCheckoutObject: {
                              message: string;
                              share: boolean;
                              allowTracking: boolean;
                              trackingEmail: string;
                            } = {
                              message: "",
                              share: true,
                              allowTracking: false,
                              trackingEmail: "",
                            };
                            if (storedCheckout) {
                              storedCheckoutObject = JSON.parse(storedCheckout);
                            }
                            storedCheckoutObject.allowTracking =
                              event.target.checked;
                            localStorage.setItem(
                              consts.STORAGE_CHECKOUT,
                              JSON.stringify(storedCheckoutObject)
                            );
                            setAllowTracking(event.target.checked);
                          }}
                        />
                      }
                      label={`Get notified when the funds are distributed`}
                    />
                  </Grid>
                )}
                {allowTracking && (
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label={`Email to send tracking notifications to`}
                      value={trackingEmail}
                      fullWidth={true}
                      InputProps={{
                        className: "tb_paper_input",
                      }}
                      onChange={(
                        event: React.ChangeEvent<
                          HTMLInputElement | HTMLTextAreaElement
                        >
                      ) => {
                        let storedCheckout = localStorage.getItem(
                          consts.STORAGE_CHECKOUT
                        );
                        let storedCheckoutObject: {
                          message: string;
                          share: boolean;
                          allowTracking: boolean;
                          trackingEmail: string;
                        } = {
                          message: "",
                          share: true,
                          allowTracking: false,
                          trackingEmail: "",
                        };
                        if (storedCheckout) {
                          storedCheckoutObject = JSON.parse(storedCheckout);
                        }
                        storedCheckoutObject.trackingEmail = event.target.value;
                        localStorage.setItem(
                          consts.STORAGE_CHECKOUT,
                          JSON.stringify(storedCheckoutObject)
                        );
                        setTrackingEmail(event.target.value);
                      }}
                    />
                  </Grid>
                )}

                <Grid size={{ xs: 12 }}>
                  <Button
                    fullWidth={true}
                    variant="outlined"
                    disabled={donations.length == 0 || busy}
                    onClick={async () => {
                      if (validateDonation()) {
                        setBusy(true);
                        await saveDonations(
                          codeDetails.code,
                          codeDetails.name,
                          codeDetails.email,
                          shareSelections,
                          message,
                          donations,
                          allowTracking,
                          trackingEmail
                        );
                        setBusy(false);
                        if (settings.confirmationDetails.confirmationSkip) {
                          handleRedirect();
                        } else {
                          setShowConfirmation(true);
                        }
                      }
                    }}
                    className="tb_action_button"
                  >
                    Finalize Donation
                  </Button>
                </Grid>
              </React.Fragment>
            )}
            {busy && (
              <Grid size={{ xs: 12 }}>
                <LinearProgress />
              </Grid>
            )}
          </Grid>
        </Paper>
      </Grid>
    );
  };

  return (
    <Grid
      size={{ xs: 12 }}
      container
      spacing={2}
      sx={{ marginTop: "32px", marginBottom: "32px" }}
    >
      {donations.length > 0 && (
        <Grid size={{ xs: 12 }}>
          <Typography variant="h5" className="tb_link">
            <Link href={settings.addCharitiesLinkUrl} underline="none">
              <ArrowBackIcon />
              <div
                style={{
                  display: "inline-block",
                  marginLeft: "6px",
                  position: "relative",
                  top: "-3px",
                }}
              >
                {settings.addCharitiesLinkText}
              </div>
            </Link>
          </Typography>
        </Grid>
      )}
      <Notification
        message={notification.message}
        type={notification.type}
        open={notification.message.length > 0}
        onDismiss={() => {
          setNotification({ message: "", type: "" });
        }}
      />
      {busy && (
        <Grid size={{ xs: 12 }}>
          <LinearProgress />
        </Grid>
      )}
      {showConfirmation && (
        <Confirmation
          donations={donations}
          settings={settings}
          codeDetails={codeDetails}
          busy={busy}
          handleClose={() => {
            handleRedirect();
          }}
        />
      )}
      {!showConfirmation && (
        <Grid size={{ xs: 12, sm: 8 }}>
          <Grid size={{ xs: 12 }} container spacing={4}>
            {donations.length == 0 ? (
              <Grid size={{ xs: 12 }}>
                <Typography
                  variant="body1"
                  component="div"
                  sx={{
                    padding: "16px 16px 16px 0px",
                    maxWidth: "550px",
                    textAlign: "center",
                  }}
                >
                  {codeDetails.code == consts.DIRECT_CODE
                    ? `Select some charities to support a cause that's
                  dear to you.`
                    : `Use your TisBest Charity Gift Card to support a cause that's
                  dear to you.`}
                  <Button
                    fullWidth={true}
                    variant="outlined"
                    onClick={() => {
                      window.location.href = settings.addCharitiesLinkUrl;
                    }}
                    className="tb_spend_button"
                  >
                    Select Charities
                  </Button>
                </Typography>
              </Grid>
            ) : (
              <Grid
                size={{ xs: 12 }}
                container
                style={{
                  marginTop: "0px",
                  paddingTop: "18px",
                  paddingBottom: "18px",
                }}
                spacing={4}
              >
                {donations.map((donation) => {
                  return (
                    <DonationInputs
                      busy={busy}
                      key={donation.ein}
                      codeDetails={codeDetails}
                      ein={donation.ein}
                      settings={settings}
                      donations={donations}
                      handleSubmit={(donation: Donation) => {
                        let updatedDonations = donations.slice();
                        let donationIndex = updatedDonations.findIndex(
                          (item) => {
                            return item.ein == donation.ein;
                          }
                        );
                        if (donationIndex >= 0) {
                          updatedDonations[donationIndex] = donation;
                        } else {
                          updatedDonations.push(donation);
                        }
                        updateDonations(updatedDonations);
                        setDonations(updatedDonations);
                      }}
                      handleRemove={(donation: Donation) => {
                        let updatedDonations = donations.slice();
                        let donationIndex = updatedDonations.findIndex(
                          (item) => {
                            return item.ein == donation.ein;
                          }
                        );
                        if (donationIndex >= 0) {
                          updatedDonations.splice(donationIndex, 1);
                        }
                        updateDonations(updatedDonations);
                        setDonations(updatedDonations);
                      }}
                    />
                  );
                })}
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
      {!showConfirmation && renderGiftCardCheckout()}
    </Grid>
  );
};
